import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n/LanguageContext";
import DOMPurify from "dompurify";
import APIService from "../services/APIService";
import SimulationHeuristicsAPI from "../services/SimulationHeuristicsAPI";
import EmailAPI from "../services/EmailAPI";
import UserAPI from "../services/UserAPI"; // Added UserAPI import for messaging handlers
import RecentSimulationsAPI from "../services/RecentSimulationsAPI"; // Added for activity tracking
import { getCookie } from "../utils/CookieUtil"; // Fixed filename case
import { useUser } from "../context/UserContext"; // Import UserContext
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faSms,
  faPhone,
  faPeopleGroup,
} from "@fortawesome/free-solid-svg-icons";
import "./css/Heuristics.css";

/**
 * Function to get tailored simulation recommendations for users
 * This is defined outside the component so it can be exported separately
 * @param {number|null} uuid - Optional user ID to filter recommendations
 * @returns {Promise<Array>} List of simulation recommendations
 */
export const getTailoredUserSimulations = async (uuid = null) => {
  try {
    const result =
      await SimulationHeuristicsAPI.getTailoredUserSimulations(uuid);
    return result;
  } catch (error) {
    throw error; // Re-throw to let the component handle it
  }
};

/**
 * Heuristics component for displaying tailored simulation recommendations based on user behavior patterns.
 * Fetches simulation heuristics data from the backend API and presents tailored recommendations.
 */
const SimulationHeuristics = () => {
  // Get selected user from UserContext (defaults to logged in user)
  const { selectedUser, loggedInUser } = useUser();
  const { t } = useTranslation();

  const [simulationMessages, setSimulationMessages] = useState([]);
  const [error, setError] = useState(null);
  const [currentUuid, setCurrentUuid] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [messageDetails, setMessageDetails] = useState({});
  const [sendingStatus, setSendingStatus] = useState({
    message: "",
    isError: false,
  });

  // Helper function to render channel icon
  const getChannelIcon = (channel) => {
    const channelLower = channel?.toLowerCase() || "";

    switch (channelLower) {
      case "email":
        return (
          <FontAwesomeIcon icon={faEnvelope} className="channel-icon email" />
        );
      case "sms":
        return <FontAwesomeIcon icon={faSms} className="channel-icon sms" />;
      case "voice":
        return (
          <FontAwesomeIcon icon={faPhone} className="channel-icon voice" />
        );
      case "social-media":
      case "social media":
        return (
          <FontAwesomeIcon
            icon={faPeopleGroup}
            className="channel-icon social-media"
          />
        );
      default:
        return <span className="channel-text">{channel}</span>;
    }
  };

  // Internal method for component to fetch simulation recommendations
  const fetchSimulations = async (uuid = null) => {
    try {
      console.log(
        "[Heuristics][fetchSimulations] Fetching tailored simulations for uuid:",
        uuid,
      );
      const response = await getTailoredUserSimulations(uuid);
      console.log(
        "[Heuristics][fetchSimulations] Received simulation response.messageMatches:",
        response.messageMatches,
      );

      // Use our helper function to normalize the data structure
      const simulationsArray = response.messageMatches
        ? response.messageMatches
        : [];
      console.log(
        "[Heuristics][fetchSimulations] Normalized simulations array:",
        simulationsArray,
      );

      // If we have messageMatches, fetch details for each message
      if (simulationsArray.length > 0) {
        // Fetch message details for each message UUID in parallel
        const messageUuids = simulationsArray.map((item) => item.messageUuid);
        console.log(
          "[Heuristics][fetchSimulations] Found message UUIDs to fetch details for:",
          messageUuids,
        );
        try {
          // Fetch all messages in parallel
          let simulationMessageArray = [];
          const messagePromises = messageUuids.map((uuid) =>
            fetchMessageDetails(uuid),
          );
          console.log(
            "[Heuristics][fetchSimulations] Started fetching",
            messagePromises.length,
            "message details",
          );
          const results = await Promise.allSettled(messagePromises);
          console.log(
            "[Heuristics][fetchSimulations] Message detail fetch results:",
            results,
          );

          // Extract successful responses into simulationMessages
          simulationMessageArray = results
            .filter((result) => result.status === "fulfilled" && result.value)
            .map((result) => result.value);

          console.log(
            "[Heuristics][fetchSimulations] Processed simulation messages:",
            simulationMessageArray,
          );

          // Update state with the fetched message details
          setSimulationMessages(simulationMessageArray);
          return simulationMessageArray;
        } catch (err) {
          // Error handling - silently continue
          console.error(
            "[Heuristics][fetchSimulations] Error fetching message details:",
            err,
          );
        }
        return simulationsArray;
      }

      return simulationsArray;
    } catch (error) {
      setError(
        "Failed to load simulation recommendations. Please try again later.",
      );
      return [];
    }
  };

  // Get the current user's tailored simulation recommendations
  const getCurrentUserSimulations = async () => {
    // Use selectedUser from UserContext, fallback to loggedInUser if no selection
    const currentUser = selectedUser || loggedInUser;

    if (!currentUser || !currentUser.uuid) {
      setError(
        "No user selected. Please select a user from the User List or log in.",
      );
      return;
    }

    try {
      console.log(
        "[Heuristics] Loading simulations for user:",
        currentUser.uuid,
        currentUser.email,
      );

      setCurrentUuid(currentUser.uuid);
      setCurrentUserEmail(currentUser.email || "No email provided");

      // Get tailored simulations for this specific user - fetchSimulations already sets the state
      await fetchSimulations(currentUser.uuid);
    } catch (error) {
      console.error("[Heuristics] Error loading user simulations:", error);
      setError("Failed to load user information. Please try again later.");
    }
  };

  // Function to fetch message details by UUID
  const fetchMessageDetails = async (messageUuid) => {
    if (messageDetails[messageUuid]) {
      // If we already have the message details, no need to fetch again
      return messageDetails[messageUuid];
    }

    try {
      // Fetch the message details
      const message = await SimulationHeuristicsAPI.getMessageById(messageUuid);

      // Store the message details in state
      setMessageDetails((prev) => ({
        ...prev,
        [messageUuid]: message,
      }));

      return message;
    } catch (error) {
      return null;
    }
  };

  // Function to handle sending a message using EmailAPI
  // Only send to current user's email
  const handleSendMessage = (message) => {
    // Check the communication channel and call the appropriate handler
    if (!message || !message.communicationChannel) {
      console.error(
        "Invalid message or missing communication channel",
        message,
      );
      setSendingStatus({
        message:
          "Error: Cannot send message with missing communication channel",
        isError: true,
      });
      return;
    }

    const channel = message.communicationChannel.toLowerCase();
    if (channel === "email") {
      handleSendEmail(message);
    } else if (channel === "sms") {
      handleSendSMS(message);
    } else if (channel === "voice") {
      handleSendVoice(message);
    } else if (channel === "social-media") {
      handleSendSocialMedia(message);
    } else {
      console.warn("Unsupported communication channel:", channel);
      setSendingStatus({
        message: `Unsupported communication channel: ${message.communicationChannel}`,
        isError: true,
      });
    }
  };
  const handleSendEmail = async (message) => {
    try {
      // Get current user information
      if (!currentUuid || !currentUserEmail) {
        throw new Error("No user selected to send email to");
      }

      console.log(
        "Preparing to send email simulation for user:",
        currentUuid,
        "with email:",
        currentUserEmail,
      );

      // Get the user ID from UUID
      const userResponse = await UserAPI.getUserByUuid(currentUuid);

      console.log("Fetched user response for email simulation:", userResponse);

      if (!userResponse || !userResponse.uuid) {
        throw new Error("Unable to retrieve user information");
      }

      // Prepare the communication simulation data for the backend
      // Only send essential information - let backend retrieve everything from uuid
      const communicationSimulationData = {
        uuid: userResponse.uuid,
        channelType: "EMAIL",
        activityType: "SIMULATION",
        messageId: message.messageId,
        status: "PENDING",
      };

      console.log(
        "Communication simulation data prepared for sending:",
        communicationSimulationData,
      );

      // Send through SimulationHeuristicsAPI which will call /api/communication-simulations
      const response = await SimulationHeuristicsAPI.sendEmailSimulation(
        communicationSimulationData,
      );

      console.log("Communication simulation response:", response);

      // Display a success message
      setSendingStatus({
        message: "Message sent successfully to " + currentUserEmail,
        isError: false,
      });

      // Show toast for immediate feedback
      toast.success(
        t("simulation.toast.emailSent").replace("{email}", currentUserEmail),
      );
    } catch (error) {
      console.error("Error sending email simulation:", error);

      // Display error message
      setSendingStatus({
        message: "Failed to send message: " + error.message,
        isError: true,
      });

      // Show error toast
      toast.error(
        t("simulation.toast.emailFailed").replace("{error}", error.message),
      );
    }
  };

  // Handler for sending SMS messages
  const handleSendSMS = async (message) => {
    try {
      // Check if user has a phone number
      if (!currentUuid) {
        throw new Error("No user selected to send SMS to");
      }

      console.log("Preparing to send SMS simulation for user:", currentUuid);

      // Get the user information
      const userResponse = await UserAPI.getUserByUuid(currentUuid);

      console.log("Fetched user response for SMS simulation:", userResponse);

      if (!userResponse || !userResponse.uuid) {
        throw new Error("Unable to retrieve user information");
      }

      if (!userResponse.phone) {
        throw new Error("User does not have a phone number on file");
      }

      // Prepare the communication simulation data for the backend
      // Only send essential information - let backend retrieve everything from uuid
      const communicationSimulationData = {
        uuid: userResponse.uuid,
        channelType: "SMS",
        activityType: "SIMULATION",
        messageId: message.messageId,
        status: "PENDING",
      };

      console.log(
        "Communication simulation data prepared for SMS sending:",
        communicationSimulationData,
      );

      // Send through SimulationHeuristicsAPI which will call /api/communication-simulations
      const response = await SimulationHeuristicsAPI.sendEmailSimulation(
        communicationSimulationData,
      );

      console.log("SMS communication simulation response:", response);

      // Display a success message
      setSendingStatus({
        message: "SMS sent successfully to " + userResponse.phone,
        isError: false,
      });

      // Show toast for immediate feedback
      toast.success(
        t("simulation.toast.smsSent").replace("{phone}", userResponse.phone),
      );
    } catch (error) {
      console.error("Error sending SMS simulation:", error);

      // Display error message
      setSendingStatus({
        message: "Failed to send SMS: " + error.message,
        isError: true,
      });

      // Show error toast
      toast.error(
        t("simulation.toast.smsFailed").replace("{error}", error.message),
      );
    }
  };

  // Handler for sending voice messages
  const handleSendVoice = async (message) => {
    try {
      // Check if user has a phone number
      if (!currentUuid) {
        throw new Error("No user selected to place voice call to");
      }

      // Fetch the user's phone number from their profile
      const userResponse = await UserAPI.getUserByUuid(currentUuid);
      const userPhone = userResponse.phone;

      if (!userPhone) {
        throw new Error("User does not have a phone number on file");
      }

      // Prepare voice call data
      const voiceData = {
        recipientPhone: userPhone,
        messageScript: message.content || "",
        messageId: message.messageId,
        sophisticationLevel: message.sophisticationLevel,
        communicationChannel: "Voice",
        communicationContext: message.communicationContext,
        callDuration: "60", // Estimated duration in seconds
        matchScore: message.matchScore || "N/A",
      }; // In a real implementation, this would call a voice API service like Twilio
      // For now, we'll just log the data and simulate success
      console.log("Voice call data prepared:", voiceData);

      // Write to RecentSimulations backend for tracking
      try {
        await RecentSimulationsAPI.writeActivity(voiceData, "voice");
      } catch (activityError) {
        console.error(
          "Failed to write voice activity to recent activities:",
          activityError,
        );
      }

      // Display success message
      setSendingStatus({
        message: `Voice call would be placed to ${userPhone}`,
        isError: false,
      });

      // Show toast for immediate feedback
      toast(`Voice call simulation: Call would be placed to ${userPhone}`, {
        icon: "ℹ️",
      });
    } catch (error) {
      console.error("Error setting up voice call:", error);
      setSendingStatus({
        message: `Error setting up voice call: ${error.message}`,
        isError: true,
      });
      toast.error(
        t("simulation.toast.error").replace("{error}", error.message),
      );
    }
  };

  // Handler for sending social media messages
  const handleSendSocialMedia = async (message) => {
    try {
      // Check if we have a current user
      if (!currentUuid) {
        throw new Error("No user selected to send social media message to");
      }

      // In a real implementation, we would fetch the user's social media accounts
      const socialMediaTypes = ["Facebook", "LinkedIn", "Twitter", "Instagram"];
      const selectedPlatform =
        socialMediaTypes[Math.floor(Math.random() * socialMediaTypes.length)];

      // Prepare social media message data
      const socialData = {
        uuid: currentUuid,
        platform: selectedPlatform,
        messageContent: message.content || "",
        messageId: message.messageId,
        sophisticationLevel: message.sophisticationLevel,
        communicationChannel: "Social-Media",
        communicationContext: message.communicationContext,
        isSimulation: true,
        matchScore: message.matchScore || "N/A",
      }; // In a real implementation, this would interface with social media APIs
      // For now, we'll just log the data and simulate success
      console.log("Social media message prepared:", socialData);

      // Write to RecentSimulations backend for tracking
      try {
        await RecentSimulationsAPI.writeActivity(socialData, "social-media");
      } catch (activityError) {
        console.error(
          "Failed to write social media activity to recent activities:",
          activityError,
        );
      }

      // Display success message
      setSendingStatus({
        message: `Social media message would be sent via ${selectedPlatform}`,
        isError: false,
      });

      // Show toast for immediate feedback
      toast(
        `Social media simulation: Message would be sent via ${selectedPlatform}`,
        { icon: "ℹ️" },
      );

      // Track this interaction in the user's simulation history (would store in the database in real implementation)
      console.log(
        "Tracking social media simulation interaction for user:",
        currentUuid,
      );
    } catch (error) {
      console.error("Error sending social media message:", error);
      setSendingStatus({
        message: `Error sending social media message: ${error.message}`,
        isError: true,
      });
      toast.error(
        t("simulation.toast.error").replace("{error}", error.message),
      );
    }
  };

  useEffect(() => {
    // When the component mounts or when selected user changes, load their tailored simulations
    getCurrentUserSimulations();

    return () => {
      // Cleanup function - use EmailAPI to clear data
      EmailAPI.clearEmailDataFromStorage();
    };
  }, [selectedUser, loggedInUser]); // Re-run when selectedUser or loggedInUser changes

  if (error) {
    return <div className="heuristics-error">{error}</div>;
  }

  // Process messages from simulationMessages for rendering
  const processedMessages = simulationMessages.map((message) => {
    return {
      messageId: message.messageId || "N/A",
      subject: message.subject || "No Subject",
      content: message.content || "No Content",
      communicationContext: message.communicationContext || "Unknown",
      communicationChannel: message.communicationChannel || "N/A",
      sophisticationLevel: message.sophisticationLevel || "N/A",
      matchScore: message.matchScore || "N/A",
      // Add any other relevant properties from the message object
    };
  });

  return (
    <div className="heuristics-container">
      <h2>Available Messages to Send</h2>{" "}
      {processedMessages.length > 0 ? (
        <div className="messages-table-container">
          <table className="messages-table">
            <thead>
              <tr>
                <th>Message ID</th>
                <th>Subject</th>
                <th>Content</th>
                <th>Context</th>
                <th>Channel</th>
                <th>Level</th>
                <th>Match Score</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {processedMessages.map((message, index) => (
                <tr key={index} className="message-row">
                  <td>{message.messageId}</td>
                  <td>{message.subject}</td>
                  <td className="message-content-cell">
                    <div className="message-content">
                      <div className="message-body">
                        {message.content &&
                        message.content.includes("<") &&
                        message.content.includes(">") ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(message.content, {
                                ALLOWED_TAGS: [
                                  "p",
                                  "br",
                                  "strong",
                                  "em",
                                  "u",
                                  "a",
                                ],
                                ALLOWED_ATTR: ["href", "target", "rel"],
                              }),
                            }}
                          />
                        ) : (
                          <p>{message.content}</p>
                        )}
                      </div>{" "}
                    </div>
                  </td>
                  <td>{message.communicationContext}</td>
                  <td className="channel-cell">
                    {getChannelIcon(message.communicationChannel)}
                  </td>
                  <td>{message.sophisticationLevel}</td>
                  <td className="match-score-cell">{message.matchScore}</td>
                  <td>
                    <button
                      className="send-message-btn"
                      onClick={() => handleSendMessage(message)}
                      disabled={
                        message.communicationChannel?.toLowerCase() !==
                          "email" &&
                        message.communicationChannel?.toLowerCase() !== "sms"
                      }
                      title={
                        message.communicationChannel?.toLowerCase() !==
                          "email" &&
                        message.communicationChannel?.toLowerCase() !== "sms"
                          ? `${message.communicationChannel} messages are not yet supported. Only email and SMS messages can be sent.`
                          : "Send this message"
                      }
                    >
                      Send Message
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No messages available to display.</p>
      )}
      {/* Admin section - only shown for admin users (you'll need to implement admin check) */}
      {true && (
        <div className="admin-actions">
          <h3>Admin Actions</h3>
          <button
            onClick={() => fetchSimulations(currentUuid)}
            className="admin-btn"
          >
            View All User Recommendations
          </button>
        </div>
      )}
    </div>
  );
};

// Export the Heuristics component as default
export default SimulationHeuristics;
